import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import { Icon, TEXT_ALIGNMENT_OPTIONS, TextAlignmentOptions } from '#const';
import { getIconUrl } from '#utils';

const Button = dynamic(() => import('./Button'));

export const defaultOptions = {
	variant: 'Default',
	copy: 'See if I Qualify',
	usePageColors: false,
	color: 'rgba(17, 186, 0, 1)',
	textColor: '#FFF',
	arrow: true,
	buttonAlignment: TextAlignmentOptions.LEFT,
	openModal: false,
	scrollTo: false,
	href: '#form',
	wideButton: false,
	logPostHogEvent: false,
	desktopCopy: 'Call Now',
	mobileCopy: 'Call Now',
	phoneNumber: '(555) 555-5555',
	showDynamicPhoneNumber: false,
} as const;

Builder.registerComponent(Button, {
	name: 'Button',
	inputs: [
		{
			name: 'variant',
			friendlyName: 'Variant',
			type: 'string',
			enum: [
				{ label: 'Default', value: 'Default' },
				{ label: 'Phone CTA', value: 'Phone CTA' },
			],
			defaultValue: defaultOptions.variant,
			helperText:
				'Default: standard CTA button. Phone CTA: telephone link with dynamic phone number support.',
		},

		// ── Default variant text ──────────────────────────────────────────
		{
			name: 'copy',
			friendlyName: 'Button Text',
			type: 'string',
			required: true,
			defaultValue: defaultOptions.copy,
			showIf: function (options) {
				return options.get('variant') !== 'Phone CTA';
			},
		},
		// ── Phone CTA variant text ────────────────────────────────────────
		{
			name: 'desktopCopy',
			friendlyName: 'Desktop Copy',
			type: 'string',
			defaultValue: defaultOptions.desktopCopy,
			helperText:
				'Text shown on desktop. Type {{phoneNumber}} anywhere in this field and it will be replaced with the actual phone number. Example: "Call {{phoneNumber}} Today" → "Call (555) 234-5678 Today".',
			showIf: function (options) {
				return options.get('variant') === 'Phone CTA';
			},
		},
		{
			name: 'mobileCopy',
			friendlyName: 'Mobile Copy',
			type: 'string',
			defaultValue: defaultOptions.mobileCopy,
			helperText: 'Text shown on mobile devices.',
			showIf: function (options) {
				return options.get('variant') === 'Phone CTA';
			},
		},
		{
			name: 'showDynamicPhoneNumber',
			friendlyName: 'Use Dynamic Phone Number',
			type: 'boolean',
			defaultValue: defaultOptions.showDynamicPhoneNumber,
			helperText:
				'When enabled, overrides the phone number using dynamic phone number from URL parameters (ef_affiliate_id & ef_offer_id).',
			showIf: function (options) {
				return options.get('variant') === 'Phone CTA';
			},
		},
		// ── Phone CTA phone number ────────────────────────────────────────
		{
			name: 'phoneNumber',
			friendlyName: 'Phone Number',
			type: 'string',
			defaultValue: defaultOptions.phoneNumber,
			helperText: 'US phone number, e.g. (555) 234-5678 or 5552345678.',
			// Accepts common US formats; the component normalises to digits and
			// validates with NANP_DIGITS_REGEX (see types.ts) before building the tel: href.
			regex: {
				pattern: '^\\+?1?[\\s.-]*\\(?[2-9]\\d{2}\\)?[\\s.-]*[2-9]\\d{2}[\\s.-]*\\d{4}$',
				message:
					'Enter a valid 10-digit US phone number (area code and exchange must start with 2–9).',
			},
			showIf: function (options) {
				return options.get('variant') === 'Phone CTA';
			},
		},

		// ── Default variant link ──────────────────────────────────────────
		{
			name: 'href',
			friendlyName: 'Link',
			type: 'string',
			defaultValue: defaultOptions.href,
			helperText: `Full URL or section ID (e.g. "#form") that the button should link to.`,
			showIf: function (options) {
				return options.get('variant') !== 'Phone CTA' && options.get('openModal') !== true;
			},
		},
		{
			name: 'usePageColors',
			friendlyName: 'Use Page Colors',
			type: 'boolean',
			defaultValue: defaultOptions.usePageColors,
			helperText: `If true, will use colors set on page for styling;
				if false, use the controls below`,
		},
		{
			name: 'color',
			friendlyName: 'Button Color',
			type: 'color',
			defaultValue: defaultOptions.color,
			helperText: 'Be sure to select a color dark enough for the white text to legible.',
			showIf: function (options) {
				return options.get('usePageColors') !== true;
			},
		},
		{
			name: 'textColor',
			friendlyName: 'Text Color',
			type: 'color',
			defaultValue: defaultOptions.textColor,
			helperText: 'Be sure to select a color light enough for the text to have contrast.',
		},
		{
			name: 'arrow',
			friendlyName: 'Show Arrow Icon',
			type: 'boolean',
			defaultValue: defaultOptions.arrow,
		},
		{
			name: 'hover',
			friendlyName: 'Hover Color',
			type: 'color',
			helperText: `Optional color for when user hovers button. 
				Be sure to select a color dark enough for the text to legible.`,
		},
		{
			name: 'buttonAlignment',
			friendlyName: 'Button Alignment',
			type: 'string',
			enum: [...TEXT_ALIGNMENT_OPTIONS],
			defaultValue: defaultOptions.buttonAlignment,
			helperText: 'Optional alignment for the button.',
		},

		// ── Default variant behavior ──────────────────────────────────────
		{
			name: 'openModal',
			friendlyName: 'Open Form Modal',
			type: 'boolean',
			defaultValue: defaultOptions.openModal,
			helperText: `Choose true if this button should open the FormModal instead of navigating. 
				Note: A FormModal component must be present on the page for this to work.`,
			showIf: function (options) {
				return options.get('variant') !== 'Phone CTA';
			},
		},
		{
			name: 'scrollTo',
			friendlyName: 'Scroll within page',
			type: 'boolean',
			defaultValue: defaultOptions.scrollTo,
			helperText: `Choose true if this button will scroll the user to an
				ID on the page. The href should be an id like "#form"`,
			showIf: function (options) {
				return options.get('variant') !== 'Phone CTA' && options.get('openModal') !== true;
			},
		},
		{
			name: 'wideButton',
			friendlyName: 'Wide Button',
			type: 'boolean',
			defaultValue: defaultOptions.wideButton,
			helperText: 'Add extra horizontal padding to the button',
		},

		// ── PostHog tracking ──────────────────────────────────────────────
		{
			name: 'logPostHogEvent',
			friendlyName: 'Log PostHog event upon Click',
			type: 'boolean',
			defaultValue: defaultOptions.logPostHogEvent,
			helperText: 'Enable PostHog event tracking when button is clicked',
			showIf: function (options) {
				return options.get('variant') === 'Phone CTA' || options.get('openModal') !== true;
			},
		},
		{
			name: 'buttonId',
			friendlyName: 'Button ID',
			type: 'string',
			helperText: 'Unique identifier for PostHog event tracking, e.g. more_info',
			showIf: function (options) {
				return options.get('logPostHogEvent') === true || options.get('openModal') === true;
			},
		},
	],
	image: getIconUrl(Icon.BUTTON),
});
